import { createAction, props } from '@ngrx/store';
import { UserListResponse, GetUserListParams } from '../models/users.model';

/**
 * Load user list action
 */
export const loadUserList = createAction(
  '[User List] Load User List',
  props<{ params?: GetUserListParams }>()
);

/**
 * Load user list success action
 */
export const loadUserListSuccess = createAction(
  '[User List] Load User List Success',
  props<{ response: UserListResponse }>()
);

/**
 * Load user list failure action
 */
export const loadUserListFailure = createAction(
  '[User List] Load User List Failure',
  props<{ error: string }>()
);

/**
 * Set user list search action
 */
export const setUserListSearch = createAction(
  '[User List] Set Search',
  props<{ search: string }>()
);

/**
 * Set user list pagination action
 */
export const setUserListPagination = createAction(
  '[User List] Set Pagination',
  props<{ pageIndex: number; pageSize: number }>()
);

/**
 * Set user list sort action
 */
export const setUserListSort = createAction(
  '[User List] Set Sort',
  props<{ active: string; direction: 'asc' | 'desc' }>()
);

/**
 * Clear user list error action
 */
export const clearUserListError = createAction('[User List] Clear Error');

/**
 * Freeze user action
 */
export const freezeUser = createAction(
  '[User List] Freeze User',
  props<{ userId: string }>()
);

/**
 * Freeze user success action
 */
export const freezeUserSuccess = createAction(
  '[User List] Freeze User Success',
  props<{ userId: string }>()
);

/**
 * Freeze user failure action
 */
export const freezeUserFailure = createAction(
  '[User List] Freeze User Failure',
  props<{ error: string }>()
);

/**
 * Unfreeze user action
 */
export const unfreezeUser = createAction(
  '[User List] Unfreeze User',
  props<{ userId: string }>()
);

/**
 * Unfreeze user success action
 */
export const unfreezeUserSuccess = createAction(
  '[User List] Unfreeze User Success',
  props<{ userId: string }>()
);

/**
 * Unfreeze user failure action
 */
export const unfreezeUserFailure = createAction(
  '[User List] Unfreeze User Failure',
  props<{ error: string }>()
);

/**
 * Block user action
 */
export const blockUser = createAction(
  '[User List] Block User',
  props<{ userId: string }>()
);

/**
 * Block user success action
 */
export const blockUserSuccess = createAction(
  '[User List] Block User Success',
  props<{ userId: string }>()
);

/**
 * Block user failure action
 */
export const blockUserFailure = createAction(
  '[User List] Block User Failure',
  props<{ error: string }>()
);

/**
 * Unblock user action
 */
export const unblockUser = createAction(
  '[User List] Unblock User',
  props<{ userId: string }>()
);

/**
 * Unblock user success action
 */
export const unblockUserSuccess = createAction(
  '[User List] Unblock User Success',
  props<{ userId: string }>()
);

/**
 * Unblock user failure action
 */
export const unblockUserFailure = createAction(
  '[User List] Unblock User Failure',
  props<{ error: string }>()
);

/**
 * Update user role action
 */
export const updateUserRole = createAction(
  '[User List] Update User Role',
  props<{ userId: string; role: string }>()
);

/**
 * Update user role success action
 */
export const updateUserRoleSuccess = createAction(
  '[User List] Update User Role Success',
  props<{ userId: string; role: string }>()
);

/**
 * Update user role failure action.
 *
 * `errorCode` (when present) is the backend error code so the UI can
 * branch on specific failures — e.g. `DUPLICATE_PENDING_APPROVAL` opens a
 * dedicated modal rather than surfacing a generic toast.
 */
export const updateUserRoleFailure = createAction(
  '[User List] Update User Role Failure',
  props<{ error: string; errorCode?: string }>()
);

/**
 * Add user to wishlist action
 */
export const addUserToWishlist = createAction(
  '[User List] Add User To Wishlist',
  props<{ email: string; phone: string; role: string }>()
);

/**
 * Add user to wishlist success action
 */
export const addUserToWishlistSuccess = createAction(
  '[User List] Add User To Wishlist Success'
);

/**
 * Add user to wishlist failure action
 */
export const addUserToWishlistFailure = createAction(
  '[User List] Add User To Wishlist Failure',
  props<{ error: string }>()
);

/**
 * Update wishlist user action
 */
export const updateWishlistUser = createAction(
  '[User List] Update Wishlist User',
  props<{ userId: string; email: string; phone: string; role: string }>()
);

/**
 * Update wishlist user success action
 */
export const updateWishlistUserSuccess = createAction(
  '[User List] Update Wishlist User Success'
);

/**
 * Update wishlist user failure action
 */
export const updateWishlistUserFailure = createAction(
  '[User List] Update Wishlist User Failure',
  props<{ error: string }>()
);

/**
 * Delete wishlist user action
 */
export const deleteUser = createAction(
  '[User List] Delete User',
  props<{ userId: string }>()
);

/**
 * Delete wishlist user success action
 */
export const deleteUserSuccess = createAction(
  '[User List] Delete User Success'
);

/**
 * Delete user failure action.
 *
 * `errorCode` (when present) is the backend error code so the UI can
 * branch on specific failures — e.g. `DUPLICATE_PENDING_APPROVAL` opens a
 * dedicated modal rather than surfacing a generic toast.
 */
export const deleteUserFailure = createAction(
  '[User List] Delete User Failure',
  props<{ error: string; errorCode?: string }>()
);

/**
 * User List Actions - Barrel export for convenience
 */
export const UserListActions = {
  loadUserList,
  loadUserListSuccess,
  loadUserListFailure,
  setUserListSearch,
  setUserListPagination,
  setUserListSort,
  clearUserListError,
  freezeUser,
  freezeUserSuccess,
  freezeUserFailure,
  unfreezeUser,
  unfreezeUserSuccess,
  unfreezeUserFailure,
  blockUser,
  blockUserSuccess,
  blockUserFailure,
  unblockUser,
  unblockUserSuccess,
  unblockUserFailure,
  updateUserRole,
  updateUserRoleSuccess,
  updateUserRoleFailure,
  addUserToWishlist,
  addUserToWishlistSuccess,
  addUserToWishlistFailure,
  updateWishlistUser,
  updateWishlistUserSuccess,
  updateWishlistUserFailure,
  deleteUser,
  deleteUserSuccess,
  deleteUserFailure,
};
